import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from '@/features/store-settings';

import type { ChatbotProviderDescriptor } from '../../model/types';
import { FieldSourceChip } from './FieldSourceChip';

export interface ModelFieldProps {
  /** `null` while following the platform default. */
  provider: string | null;
  model: string | null;
  /** True while a saved model is pending reset to the platform default (design §7.3 row 3). */
  pending: boolean;
  platformDefaultProvider: string;
  platformDefaultModel: string;
  providers: ChatbotProviderDescriptor[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  externalProviderPlatformAllowed: boolean;
  disabled?: boolean;
  onChange: (provider: string, model: string) => void;
  onChooseOther: () => void;
  onUseDefault: () => void;
  onKeepYours: () => void;
}

/**
 * `provider`/`model` as one unit (AI card doc §7.3) — three display states: Platform default
 * (read-only text), Chosen (editable provider select + model text field), Reset pending (dashed
 * chip, showing the platform default again until saved).
 */
export function ModelField({
  provider,
  model,
  pending,
  platformDefaultProvider,
  platformDefaultModel,
  providers,
  isLoading,
  isError,
  onRetry,
  externalProviderPlatformAllowed,
  disabled = false,
  onChange,
  onChooseOther,
  onUseDefault,
  onKeepYours,
}: ModelFieldProps) {
  const { t } = useTranslation('chatbot');

  const providerLabel = (key: string) => t(`ai.providers.${key}`, key);
  const isFollowingDefault = !pending && provider === null;

  const label = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography component="span" variant="inherit">
        {t('ai.model.label')}
      </Typography>
      {isFollowingDefault || pending ? (
        <FieldSourceChip source="DEFAULT" pending={pending} />
      ) : null}
      {!isFollowingDefault && !pending ? (
        <Tooltip title={t('provenance.useDefaultAria', { field: t('ai.model.label') })}>
          <IconButton
            size="small"
            aria-label={t('provenance.useDefaultAria', { field: t('ai.model.label') })}
            onClick={onUseDefault}
            disabled={disabled}
          >
            <RestartAltRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
      {pending ? (
        <Tooltip title={t('provenance.keepYoursAria', { field: t('ai.model.label') })}>
          <IconButton
            size="small"
            aria-label={t('provenance.keepYoursAria', { field: t('ai.model.label') })}
            onClick={onKeepYours}
            disabled={disabled}
          >
            <UndoRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );

  if (isFollowingDefault) {
    return (
      <FieldRow label={label} description={t('ai.model.description')}>
        <Stack spacing={1}>
          <Typography variant="body2">
            {providerLabel(platformDefaultProvider)} ·{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              {platformDefaultModel}
            </Typography>
          </Typography>
          <Button
            size="small"
            onClick={onChooseOther}
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('ai.model.chooseOther')}
          </Button>
        </Stack>
      </FieldRow>
    );
  }

  if (pending) {
    return (
      <FieldRow label={label} description={t('ai.model.description')}>
        <Typography variant="body2" color="text.secondary">
          {providerLabel(platformDefaultProvider)} ·{' '}
          <Typography component="span" sx={{ fontFamily: 'monospace' }}>
            {platformDefaultModel}
          </Typography>
        </Typography>
      </FieldRow>
    );
  }

  const currentProvider = provider ?? '';
  const providerKnown = (providers ?? []).some((p) => p.key === currentProvider);

  return (
    <FieldRow
      label={label}
      description={
        <>
          {t('ai.model.description')}
          <Typography component="span" display="block" variant="caption" color="text.secondary">
            {t('provenance.platformDefaultValue', {
              value: `${providerLabel(platformDefaultProvider)} · ${platformDefaultModel}`,
            })}
          </Typography>
        </>
      }
    >
      <Stack spacing={1}>
        {isLoading ? (
          <Skeleton variant="rounded" height={40} />
        ) : isError ? (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={onRetry}>
                {t('dictionaries.retry')}
              </Button>
            }
          >
            {t('dictionaries.loadError')}
          </Alert>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label={t('ai.model.provider')}
              value={currentProvider}
              disabled={disabled}
              error={currentProvider !== '' && !providerKnown}
              helperText={
                currentProvider !== '' && !providerKnown
                  ? t('ai.model.unknownProvider', { provider: currentProvider })
                  : undefined
              }
              onChange={(event) => onChange(event.target.value, model ?? '')}
              sx={{ minWidth: 220 }}
            >
              {!providerKnown && currentProvider !== '' ? (
                <MenuItem value={currentProvider}>{currentProvider}</MenuItem>
              ) : null}
              {(providers ?? []).map((p) => {
                const locked = !p.onPremise && !externalProviderPlatformAllowed;
                return (
                  <MenuItem key={p.key} value={p.key} disabled={locked}>
                    {providerLabel(p.key)}
                    {!p.onPremise
                      ? ` (${t(locked ? 'ai.providers.notAllowedTag' : 'ai.providers.externalTag')})`
                      : ''}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              id="ai-model-name-input"
              size="small"
              label={t('ai.model.name')}
              value={model ?? ''}
              disabled={disabled}
              placeholder={
                currentProvider === platformDefaultProvider
                  ? t('ai.model.namePlaceholder', { model: platformDefaultModel })
                  : undefined
              }
              onChange={(event) => onChange(currentProvider, event.target.value)}
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              sx={{ flexGrow: 1 }}
            />
          </Stack>
        )}
      </Stack>
    </FieldRow>
  );
}
